<script setup>
// 节流函数
const throttle = (func, limit) => {
    let lastFunc = null
    let lastRan = null

    return function (...args) {
        const context = this

        if (!lastRan) {
            func.apply(context, args)
            lastRan = Date.now()
            return
        }

        if (lastFunc) {
            clearTimeout(lastFunc)
        }

        const remaining = limit - (Date.now() - lastRan)

        lastFunc = setTimeout(() => {
            if (Date.now() - lastRan >= limit) {
                func.apply(context, args)
                lastRan = Date.now()
            }
        }, Math.max(remaining, 0))
    }
}

// 平滑滚动动画
const easeInOutQuad = (t) => {
    return t < 0.5
        ? 2 * t * t
        : -1 + (4 - 2 * t) * t
}

const props = defineProps({
    quickjump: {
        type: Array,
        default: () => [
            {
                id: 1,
                name: '产品优势',
                target: 'Heziyun-advantage'
            },
            {
                id: 2,
                name: '产品功能',
                target: 'Heziyun-function'
            },
            {
                id: 3,
                name: '应用场景',
                target: 'Heziyun-applicationScenarios'
            },
            {
                id: 4,
                name: '客户之声',
                target: 'Heziyun-custome'
            },
            {
                id: 5,
                name: '产品对比',
                target: 'Heziyun-characteristics'
            },
            {
                id: 6,
                name: '快速入门',
                target: 'Heziyun-quickStart'
            }
        ]
    }
})

// 当前激活区域
const activeSection = ref('')

// 是否正在执行点击滚动
const isScrolling = ref(false)

// 内容区域 IntersectionObserver
const observer = ref(null)

// 快速导航 ResizeObserver
const resizeObserver = ref(null)

// 头部 ResizeObserver
const headerResizeObserver = ref(null)

// 点击滚动结束计时器
const scrollTimeout = ref(null)

// 下划线元素
const underlineRef = ref(null)

// 快速导航根元素
const quickJumpRef = ref(null)

// 快速导航内容容器
const quickJumpContentRef = ref(null)

/**
 * 处理导航数据
 * 自动去掉 target 开头的点号
 */
const processedQuickJump = computed(() => {
    return props.quickjump.map(item => ({
        ...item,
        target: String(item.target || '').replace(/^\./, '')
    }))
})

/**
 * 获取页面头部元素
 */
const getHeaderElement = () => {
    return document.querySelector('.Heziyun-header-superclass')
}

/**
 * 动态设置 sticky 的 top 值
 *
 * 使用 header.getBoundingClientRect().bottom，
 * 可以兼容头部存在 top 间距、固定定位、不同高度等情况。
 */
const updateStickyTop = () => {
    const header = getHeaderElement()
    const quickJump = quickJumpRef.value

    if (!quickJump) return

    // 页面中没有头部时使用 0
    if (!header) {
        quickJump.style.setProperty(
            '--heziyun-quick-jump-top',
            '0px'
        )
        return
    }

    const headerRect = header.getBoundingClientRect()

    /*
     * headerRect.bottom 表示头部底部距离视口顶部的位置。
     *
     * 例如：
     * header top: 15px
     * header height: 64px
     * 最终 sticky top: 79px
     */
    const stickyTop = Math.max(0, headerRect.bottom)

    quickJump.style.setProperty(
        '--heziyun-quick-jump-top',
        `${stickyTop}px`
    )
}

/**
 * 更新激活项下划线位置
 */
const positionUnderline = () => {
    nextTick(() => {
        const underline = underlineRef.value
        const container = quickJumpContentRef.value

        if (!underline || !container) return

        const activeItem = container.querySelector(
            `.Heziyun-item[data-target="${activeSection.value}"]`
        )

        if (!activeItem) {
            underline.style.width = '0px'
            return
        }

        underline.style.width = `${activeItem.offsetWidth}px`
        underline.style.transform = `translateX(${activeItem.offsetLeft}px)`
    })
}

/**
 * 让移动端激活项自动滚动到可见区域
 */
const scrollActiveItemIntoView = () => {
    nextTick(() => {
        const container = quickJumpContentRef.value

        if (!container) return

        const activeItem = container.querySelector(
            `.Heziyun-item[data-target="${activeSection.value}"]`
        )

        if (!activeItem) return

        activeItem.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center'
        })
    })
}

/**
 * IntersectionObserver 监听页面内容区域
 */
const initIntersectionObserver = () => {
    if (observer.value) {
        observer.value.disconnect()
        observer.value = null
    }

    if (!window.IntersectionObserver) {
        fallbackScrollHandler()
        return
    }

    const options = {
        root: null,

        /*
         * 顶部减去头部和快速导航可能更加精准，
         * 这里保留通用配置，由 threshold 控制激活。
         */
        rootMargin: '-10% 0px -60% 0px',
        threshold: [0, 0.1, 0.3]
    }

    observer.value = new IntersectionObserver(entries => {
        if (isScrolling.value) return

        const visibleEntries = entries
            .filter(entry => entry.isIntersecting)
            .sort((a, b) => {
                return Math.abs(a.boundingClientRect.top)
                    - Math.abs(b.boundingClientRect.top)
            })

        const currentEntry = visibleEntries[0]

        if (!currentEntry) return

        const matchedItem = processedQuickJump.value.find(item => {
            return currentEntry.target.classList.contains(item.target)
        })

        if (
            matchedItem &&
            activeSection.value !== matchedItem.target
        ) {
            activeSection.value = matchedItem.target
        }
    }, options)

    processedQuickJump.value.forEach(item => {
        const target = document.querySelector(`.${item.target}`)

        if (target) {
            observer.value.observe(target)
        }
    })
}

/**
 * 监听快速导航尺寸变化
 */
const initResizeObserver = () => {
    if (resizeObserver.value) {
        resizeObserver.value.disconnect()
        resizeObserver.value = null
    }

    if (!window.ResizeObserver) return
    if (!quickJumpRef.value) return

    resizeObserver.value = new ResizeObserver(() => {
        positionUnderline()
        updateStickyTop()
    })

    resizeObserver.value.observe(quickJumpRef.value)
}

/**
 * 监听头部尺寸变化
 *
 * 如果头部响应式高度变化、菜单展开或内容变化，
 * sticky top 会自动重新计算。
 */
const initHeaderResizeObserver = () => {
    if (headerResizeObserver.value) {
        headerResizeObserver.value.disconnect()
        headerResizeObserver.value = null
    }

    const header = getHeaderElement()

    updateStickyTop()

    if (!header || !window.ResizeObserver) return

    headerResizeObserver.value = new ResizeObserver(() => {
        updateStickyTop()
    })

    headerResizeObserver.value.observe(header)
}

/**
 * 不支持 IntersectionObserver 时的滚动降级处理
 */
const handleScrollFallback = throttle(() => {
    if (isScrolling.value) return

    const quickJumpHeight = quickJumpRef.value?.offsetHeight || 0
    const header = getHeaderElement()
    const headerBottom = header
        ? Math.max(0, header.getBoundingClientRect().bottom)
        : 0

    const detectionLine = headerBottom + quickJumpHeight + 20

    let closestSection = null
    let minDistance = Infinity

    processedQuickJump.value.forEach(item => {
        const target = document.querySelector(`.${item.target}`)

        if (!target) return

        const rect = target.getBoundingClientRect()
        const distance = Math.abs(rect.top - detectionLine)

        if (distance < minDistance) {
            minDistance = distance
            closestSection = item.target
        }
    })

    if (
        closestSection &&
        closestSection !== activeSection.value
    ) {
        activeSection.value = closestSection
    }
}, 100)

/**
 * 页面滚动时更新头部位置
 *
 * 如果头部存在滚动隐藏、缩小、位移等动画，
 * sticky 导航依然能够跟随头部底部。
 */
const handleWindowScroll = throttle(() => {
    updateStickyTop()

    if (!window.IntersectionObserver) {
        handleScrollFallback()
    }
}, 16)

/**
 * 页面尺寸变化处理
 */
const handleWindowResize = throttle(() => {
    updateStickyTop()
    positionUnderline()
}, 100)

/**
 * 添加降级滚动监听
 */
const fallbackScrollHandler = () => {
    handleScrollFallback()
}

/**
 * 原生平滑滚动兼容方法
 */
const smoothScrollPolyfill = targetPosition => {
    const start = window.scrollY
    const distance = targetPosition - start
    const duration = 500

    let startTime = null

    const animateScroll = timestamp => {
        if (!startTime) {
            startTime = timestamp
        }

        const elapsed = timestamp - startTime
        const progress = Math.min(elapsed / duration, 1)
        const ease = easeInOutQuad(progress)

        window.scrollTo(
            0,
            start + distance * ease
        )

        if (progress < 1) {
            requestAnimationFrame(animateScroll)
        }
    }

    requestAnimationFrame(animateScroll)
}

/**
 * 点击导航滚动至对应区域
 */
const scrollToSection = targetClass => {
    const target = document.querySelector(`.${targetClass}`)

    if (!target) return

    isScrolling.value = true
    activeSection.value = targetClass

    const header = getHeaderElement()

    const headerBottom = header
        ? Math.max(0, header.getBoundingClientRect().bottom)
        : 0

    const quickJumpHeight = quickJumpRef.value?.offsetHeight || 0

    /*
     * 因为页面头部和快速导航都会占据顶部空间，
     * 滚动位置需要同时减去这两个元素的高度。
     */
    const scrollOffset =
        headerBottom +
        quickJumpHeight +
        20

    const targetPosition = Math.max(
        0,
        window.scrollY +
        target.getBoundingClientRect().top -
        scrollOffset
    )

    if ('scrollBehavior' in document.documentElement.style) {
        window.scrollTo({
            top: targetPosition,
            behavior: 'smooth'
        })
    } else {
        smoothScrollPolyfill(targetPosition)
    }

    if (scrollTimeout.value) {
        clearTimeout(scrollTimeout.value)
    }

    scrollTimeout.value = setTimeout(() => {
        isScrolling.value = false
        scrollTimeout.value = null

        /*
         * 滚动结束后重新计算一次当前区域，
         * 避免快速点击时激活状态不准确。
         */
        handleScrollFallback()
    }, 700)
}

/**
 * 初始化默认激活项
 */
const initActiveSection = () => {
    if (activeSection.value) return

    const firstExistingItem = processedQuickJump.value.find(item => {
        return document.querySelector(`.${item.target}`)
    })

    if (firstExistingItem) {
        activeSection.value = firstExistingItem.target
    }
}

/**
 * 清理监听器
 */
const cleanup = () => {
    if (observer.value) {
        observer.value.disconnect()
        observer.value = null
    }

    if (resizeObserver.value) {
        resizeObserver.value.disconnect()
        resizeObserver.value = null
    }

    if (headerResizeObserver.value) {
        headerResizeObserver.value.disconnect()
        headerResizeObserver.value = null
    }

    if (scrollTimeout.value) {
        clearTimeout(scrollTimeout.value)
        scrollTimeout.value = null
    }

    window.removeEventListener(
        'scroll',
        handleWindowScroll
    )

    window.removeEventListener(
        'resize',
        handleWindowResize
    )
}

/**
 * 激活项变化时更新下划线和移动端滚动位置
 */
watch(activeSection, () => {
    positionUnderline()
    scrollActiveItemIntoView()
})

/**
 * quickjump 数据变化时重新初始化监听
 */
watch(
    processedQuickJump,
    () => {
        nextTick(() => {
            initIntersectionObserver()
            initActiveSection()
            positionUnderline()
        })
    },
    {
        deep: true
    }
)

onMounted(() => {
    nextTick(() => {
        initActiveSection()
        initIntersectionObserver()
        initResizeObserver()
        initHeaderResizeObserver()

        updateStickyTop()
        positionUnderline()

        window.addEventListener(
            'scroll',
            handleWindowScroll,
            {
                passive: true
            }
        )

        window.addEventListener(
            'resize',
            handleWindowResize,
            {
                passive: true
            }
        )
    })
})

onBeforeUnmount(() => {
    cleanup()
})
</script>

<template>
    <div
        ref="quickJumpRef"
        class="Heziyun-quickJump"
    >
        <div
            ref="quickJumpContentRef"
            class="Heziyun-defaultWidth"
        >
            <button
                v-for="item in processedQuickJump"
                :key="item.id"
                type="button"
                class="Heziyun-item"
                :class="{
                    active: activeSection === item.target
                }"
                :data-target="item.target"
                @click="scrollToSection(item.target)"
            >
                {{ item.name }}
            </button>

            <div
                ref="underlineRef"
                class="Heziyun-underline"
            ></div>
        </div>
    </div>
</template>

<style scoped>
.Heziyun-quickJump {
    /*
     * JS 会自动修改这个变量。
     * 0px 是没有找到头部元素时的默认值。
     */
    --heziyun-quick-jump-top: 0px;

    position: sticky;
    top: var(--heziyun-quick-jump-top);
    z-index: 20;

    width: 100%;
    background: rgba(255, 255, 255, 0.96);
    border-bottom: 1px solid rgba(22, 119, 255, 0.08);
    box-shadow: 0 4px 16px rgba(20, 100, 255, 0.08);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);

    transition:
        top 0.2s ease,
        box-shadow 0.2s ease;

    will-change: top;
}

.Heziyun-quickJump .Heziyun-defaultWidth {
    position: relative;

    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;

    padding: 0.5rem;
}

.Heziyun-quickJump .Heziyun-item {
    appearance: none;
    flex-shrink: 0;

    margin: 0;
    padding: 0.5rem;

    color: #475467;
    font-family: inherit;
    font-size: 0.8rem;
    font-weight: 400;
    line-height: 1.5;

    background: transparent;
    border: 0;
    outline: none;
    cursor: pointer;

    transition:
        color 0.2s ease,
        font-weight 0.2s ease,
        background-color 0.2s ease;

    will-change: color;
}

.Heziyun-quickJump .Heziyun-item:hover {
    color: #216df0;
}

.Heziyun-quickJump .Heziyun-item:focus-visible {
    color: #216df0;
    background: rgba(33, 109, 240, 0.08);
    border-radius: 0.35rem;
}

.Heziyun-quickJump .Heziyun-item.active {
    color: #216df0;
    font-weight: 600;
}

.Heziyun-quickJump .Heziyun-underline {
    position: absolute;
    bottom: 0;
    left: 0;

    width: 0;
    height: 2px;

    background: #216df0;
    border-radius: 2px 2px 0 0;

    pointer-events: none;

    transition:
        width 0.3s ease,
        transform 0.3s ease;

    transform: translateX(0);
    transform-origin: left center;
}

@media (max-width: 768px) {
    .Heziyun-quickJump {
        /*
         * 不再设置 top: 15vw;
         * 顶部位置统一由 JS 根据头部高度计算。
         */
        box-shadow: 0 2px 12px rgba(20, 100, 255, 0.08);
    }

    .Heziyun-quickJump .Heziyun-defaultWidth {
        flex-wrap: nowrap;
        gap: 3vw;

        padding: 2vw 5vw;

        overflow-x: auto;
        overflow-y: hidden;

        scrollbar-width: none;
        -ms-overflow-style: none;

        scroll-behavior: smooth;
    }

    .Heziyun-quickJump .Heziyun-defaultWidth::-webkit-scrollbar {
        display: none;
    }

    .Heziyun-quickJump .Heziyun-item {
        flex-shrink: 0;

        padding: 1.5vw 2vw;

        font-size: 3.5vw;
        white-space: nowrap;
    }

    .Heziyun-quickJump .Heziyun-underline {
        height: 2px;
    }
}
</style>